import React, { useState, useEffect } from 'react';
import { getReunionTypeShort, formatReunionElectionLabel } from '../utils/reunionTypes';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useOrganisme } from '../context/OrganismeContext';
import { usePermissions } from '../hooks/usePermissions';
import {
  BuildingLibraryIcon,
  PencilSquareIcon,
  UserGroupIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import HelpButton from '../components/HelpButton';
import { helpContent } from '../data/helpContent';
import ModalPeriodesExpirees from '../components/ModalPeriodesExpirees';

export default function CompositionConseil() {
  const [activeTab, setActiveTab] = useState('composition');
  const [composition, setComposition] = useState({ actifs: [], inactifs: [] });
  const [historique, setHistorique] = useState([]);
  const [fractions, setFractions] = useState([]);
  const [fonctions, setFonctions] = useState([]);
  const [periodesEnCours, setPeriodesEnCours] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingHistorique, setLoadingHistorique] = useState(false);
  const [showInactifs, setShowInactifs] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [showModalPeriodes, setShowModalPeriodes] = useState(false);
  const [periodesExpirees, setPeriodesExpirees] = useState([]);

  const { user } = useAuth();
  const { organismeActif, isSuperAdminMode } = useOrganisme();
  const { permissions } = usePermissions();
  const navigate = useNavigate();

  const canModify = user?.role === 'super_admin' || 
    (permissions?.gestionActivite?.modifierInfosStatutaires && 
     user?.organismes?.includes(organismeActif));

  const canViewHistorique = user?.role === 'super_admin' || 
    (permissions?.gestionActivite?.consulterCompositions && 
     user?.organismes?.includes(organismeActif));

  useEffect(() => {
    if (!user || !['admin', 'super_admin'].includes(user.role)) {
      navigate('/');
      return;
    }
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, organismeActif, isSuperAdminMode]);

  useEffect(() => {
    if (activeTab === 'historique' && canViewHistorique) {
      fetchHistorique();
    }
    if (activeTab === 'documents' && canViewHistorique) {
      fetchDocuments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, organismeActif]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/conseil/${organismeActif}?includeInactifs=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComposition({
        actifs: response.data.actifs,
        inactifs: response.data.inactifs,
        derniersInactifs: response.data.derniersInactifs || {},
      });
      setFractions(response.data.fractions);
      setFonctions(response.data.fonctions);
      setPeriodesEnCours(response.data.periodesEnCours || {});
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistorique = async () => {
    setLoadingHistorique(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/historique`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHistorique(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
    } finally {
      setLoadingHistorique(false);
    }
  };

  const fetchDocuments = async () => {
    setLoadingDocuments(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/documents`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDocuments(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/documents/download?key=${encodeURIComponent(doc.key)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      alert('Erreur lors du téléchargement du document');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  /** Second collège ADAR : représentant externe ou (ancien) adhérent */
  const renderSecondCollegePersonne = (m) => {
    if (!m) return null;
    if (m.secondCollegeHorsPlateforme) {
      return (
        <>
          {m.structureSecondCollege && (
            <span className="block text-gray-600">{m.structureSecondCollege}</span>
          )}
          <span className="block text-xs text-gray-900 mt-0.5">{m.nomRepresentantSecondCollege}</span>
          {m.emailRepresentantSecondCollege && (
            <span className="block text-xs text-gray-500 mt-0.5">{m.emailRepresentantSecondCollege}</span>
          )}
        </>
      );
    }
    return (
      <span className="text-gray-900">
        {m.adherent?.prenom} {m.adherent?.nom?.toUpperCase?.()}
      </span>
    );
  };

  const titreConseil = organismeActif === 'SAR' ? 'Conseil syndical' : 'Conseil d\'administration';

  // Séparer les membres par type
  const membresBureau = composition.actifs.filter(m => m.type === 'bureau');
  // Côté AMAIR : inclure les cooptés dans le conseil, côté SAR : les exclure
  const membresConseil = organismeActif === 'AMAIR' 
    ? composition.actifs.filter(m => m.type === 'conseil')
    : composition.actifs.filter(m => m.type === 'conseil' && !m.estCoopte);
  
  // ADAR uniquement : second collège et stagiaires
  const membresSecondCollege = composition.actifs.filter(m => m.type === 'second_college');
  const membresStagiaires = composition.actifs.filter(m => m.type === 'stagiaire');

  // Générer toutes les positions possibles pour le conseil (4 fractions × 4 ordres = 16 lignes)
  const generateAllPositionsConseil = (membres) => {
    const fractionsValues = (organismeActif === 'AMAIR' || organismeActif === 'ADAR')
      ? ['1er_tiers', '2e_tiers', '3e_tiers']
      : ['1er_quart', '2e_quart', '3e_quart', '4e_quart'];
    
    const allPositions = [];
    const maxOrdre = (organismeActif === 'AMAIR' || organismeActif === 'ADAR') ? 3 : 4; // AMAIR/ADAR: 3 ordres par tiers, SAR: 4 ordres par quart
    
    fractionsValues.forEach(fraction => {
      for (let ordre = 1; ordre <= maxOrdre; ordre++) {
        const membre = membres.find(m => m.fraction === fraction && m.ordre === ordre && !m.estCoopte);
        const key = `${fraction}-${ordre}`;
        const dernierInactif = composition.derniersInactifs?.[key] || null;
        
        allPositions.push({
          fraction,
          ordre,
          membre: membre || null,
          dernierInactif: !membre ? dernierInactif : null, // Afficher le dernier inactif seulement si vacant
          estCoopte: false,
        });
      }
    });
    
    // Côté AMAIR : ajouter les membres de droit avant les cooptés
    if (organismeActif === 'AMAIR') {
      const membresDeDroit = membres.filter(m => m.estMembreDeDroit);
      
      // Trouver le dernier membre de droit inactif
      const dernierMembreDeDroitInactif = composition.inactifs
        ?.filter(m => m.type === 'conseil' && m.estMembreDeDroit)
        .sort((a, b) => new Date(b.dateRetrait) - new Date(a.dateRetrait))[0] || null;
      
      // Toujours afficher une ligne SAR, même si vacant
      if (membresDeDroit.length > 0) {
        membresDeDroit.forEach(membre => {
          allPositions.push({
            fraction: null,
            ordre: null,
            membre,
            dernierInactif: null,
            estCoopte: false,
            estMembreDeDroit: true,
          });
        });
      } else {
        // Pas de membre de droit actif : afficher la ligne avec l'ancien membre s'il existe
        allPositions.push({
          fraction: null,
          ordre: null,
          membre: null,
          dernierInactif: dernierMembreDeDroitInactif,
          estCoopte: false,
          estMembreDeDroit: true,
        });
      }
      
      const cooptes = membres.filter(m => m.estCoopte);
      cooptes.forEach(membre => {
        allPositions.push({
          fraction: null,
          ordre: null,
          membre,
          dernierInactif: null,
          estCoopte: true,
          estMembreDeDroit: false,
        });
      });
    }
    
    return allPositions;
  };

  // Générer les positions du bureau basées sur les fonctions (6 ou 7 selon organisme)
  const generateAllPositionsBureau = (membres) => {
    const fonctionsValues = (organismeActif === 'ADAR' || organismeActif === 'SAR')
      ? [
          'president',
          'vice_president',
          'vice_president_2',
          'secretaire',
          'secretaire_adjoint',
          'tresorier',
          'tresorier_adjoint'
        ]
      : [
          'president',
          'vice_president',
          'secretaire',
          'secretaire_adjoint',
          'tresorier',
          'tresorier_adjoint'
        ];
    
    return fonctionsValues.map(fonction => {
      const membre = membres.find(m => m.fonction === fonction);
      
      // Trouver le dernier membre inactif pour cette fonction
      let dernierInactif = null;
      if (!membre && composition.inactifs) {
        const inactifsAvecFonction = composition.inactifs.filter(m => m.type === 'bureau' && m.fonction === fonction);
        if (inactifsAvecFonction.length > 0) {
          // Trier par date de retrait décroissante et prendre le plus récent
          dernierInactif = inactifsAvecFonction.sort((a, b) => 
            new Date(b.dateRetrait) - new Date(a.dateRetrait)
          )[0];
        }
      }
      
      return {
        fonction,
        membre: membre || null,
        dernierInactif: !membre ? dernierInactif : null,
      };
    });
  };

  const positionsConseil = generateAllPositionsConseil(membresConseil);
  const positionsBureau = generateAllPositionsBureau(membresBureau);

  const getFonctionLabel = (value) => {
    return fonctions.find(f => f.value === value)?.label || value;
  };

  const getFractionLabel = (value) => {
    return fractions.find(f => f.value === value)?.label || value;
  };

  const getPeriodeFromDB = (membre) => {
    if (!membre?.fraction) return '-';
    
    const periode = periodesEnCours[membre.fraction];
    if (!periode) return '-';
    
    return `${periode.debut}-${periode.fin}`;
  };

  const getObservationFromDB = (membre) => {
    if (!membre?.fraction) return '-';
    
    const periode = periodesEnCours[membre.fraction];
    if (!periode) return '-';
    
    const anneeActuelle = new Date().getFullYear();
    const dureeRestante = periode.fin - anneeActuelle;
    
    if (dureeRestante < 0) {
      return `Sortant à l'AG ${periode.fin + 1}`;
    } else if (dureeRestante === 0) {
      return 'Dernière année';
    } else if (dureeRestante === 1) {
      return 'Durée restante de 1 an';
    } else {
      return `Durée restante de ${dureeRestante} ans`;
    }
  };

  // Formater la date de début (date de réunion d'élection)
  const getDateDebut = (reunionElection, dateCooptation) => {
    if (dateCooptation) {
      return new Date(dateCooptation).toLocaleDateString('fr-FR');
    }
    if (reunionElection?.date) {
      const date = new Date(reunionElection.date);
      const typeReunion = getReunionTypeShort(reunionElection.type, organismeActif);
      return `${typeReunion} ${date.toLocaleDateString('fr-FR')}`;
    }
    return '-';
  };

  const getRaisonRetraitLabel = (raison, autre) => {
    switch (raison) {
      case 'demission': return 'Démission';
      case 'non_adhesion': return 'Non-adhésion';
      case 'fin_mandat': return 'Fin de mandat';
      case 'autre': return autre || 'Autre';
      default: return raison;
    }
  };

  const handleModifierClick = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/periodes/check`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.hasExpirees) {
        setPeriodesExpirees(data.periodes);
        setShowModalPeriodes(true);
      } else {
        navigateToModifier();
      }
    } catch (error) {
      console.error('Erreur vérification périodes:', error);
      navigateToModifier();
    }
  };

  const navigateToModifier = () => {
    const url = organismeActif === 'ADAR' ? "/admin/composition-adar/modifier" :
                organismeActif === 'AMAIR' ? "/admin/composition-amair/modifier" :
                organismeActif === 'SAR' ? "/admin/composition-sar/modifier" :
                "/admin/composition-conseil/modifier";
    navigate(url);
  };

  const handleTraiterPeriodes = () => {
    setShowModalPeriodes(false);
    navigate('/admin/traiter-periodes-expirees');
  };

  const handleIgnorerPeriodes = () => {
    setShowModalPeriodes(false);
    navigateToModifier();
  };

  const getActionLabel = (action) => {
    switch (action) {
      case 'ajout': return 'Ajout';
      case 'retrait': return 'Retrait';
      case 'modification': return 'Modification';
      default: return action;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'ajout': return 'bg-green-100 text-green-800';
      case 'retrait': return 'bg-red-100 text-red-800';
      case 'modification': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BuildingLibraryIcon className="h-8 w-8 text-indigo-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Composition du Conseil et du Bureau</h1>
              <p className="text-sm text-gray-500">Gestion des membres statutaires</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {canModify && (
              <button
                onClick={handleModifierClick}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <PencilSquareIcon className="h-4 w-4" />
                Modifier la composition
              </button>
            )}
            {!isSuperAdminMode && (
              <HelpButton 
                title={helpContent.compositionConseil.title}
                sections={helpContent.compositionConseil.sections}
              />
            )}
          </div>
        </div>

        {/* Onglets */}
        <div className="flex gap-4 mt-6 border-b border-gray-200 -mb-px">
          <button
            onClick={() => setActiveTab('composition')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'composition'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <UserGroupIcon className="h-5 w-5 inline mr-2" />
            Composition actuelle
          </button>
          {canViewHistorique && (
            <button
              onClick={() => setActiveTab('historique')}
              className={`pb-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'historique'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <ClockIcon className="h-5 w-5 inline mr-2" />
              Historique des modifications
            </button>
          )}
          {canViewHistorique && (
            <button
              onClick={() => setActiveTab('documents')}
              className={`pb-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'documents'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <DocumentTextIcon className="h-5 w-5 inline mr-2" />
              Documents
            </button>
          )}
        </div>
      </div>

      <div className="px-8 py-6">
        {activeTab === 'composition' && (
          <div className="space-y-6">

            {/* Conseil */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Membres du {titreConseil} {organismeActif === "ADAR" && " issus du Premier Collège"} ({membresConseil.length})
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adhérent</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Période</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Début</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fin</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Observation</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {positionsConseil.map((position, index) => {
                      const membre = position.membre;
                      const dernierInactif = position.dernierInactif;
                      const isVacant = !membre;
                      const afficherInactif = isVacant && dernierInactif;
                      
                      // Ajouter une ligne vide avant les membres cooptés
                      const previousPosition = index > 0 ? positionsConseil[index - 1] : null;
                      const showSeparator = position.estCoopte && (!previousPosition || !previousPosition.estCoopte);
                      
                      return (
                        <>
                          {showSeparator && (
                            <tr key={`separator-coopte-${index}`} className="bg-gray-100">
                              <td colSpan="6" className="px-6 py-2"></td>
                            </tr>
                          )}
                          <tr key={position.estMembreDeDroit ? `membre-droit-${membre?._id || 'vacant'}` : position.estCoopte ? `coopte-${membre?._id}` : `${position.fraction}-${position.ordre}`} className={afficherInactif ? 'bg-red-50' : 'hover:bg-gray-50'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {position.estMembreDeDroit ? (
                              <span className="text-blue-700 font-medium">SAR</span>
                            ) : position.estCoopte ? (
                              <span className="text-honey-700 font-medium">Coopté</span>
                            ) : (
                              `${getFractionLabel(position.fraction)} / ${position.ordre}`
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {afficherInactif ? (
                              <span className="text-gray-500 line-through">
                                {dernierInactif.adherent?.prenom} {dernierInactif.adherent?.nom?.toUpperCase()}
                              </span>
                            ) : isVacant ? (
                              <span className="text-gray-400 italic">Vacant</span>
                            ) : (
                              <span className="text-gray-900">
                                {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {position.estCoopte || position.estMembreDeDroit ? '-' : (
                              afficherInactif ? (
                                <span className="line-through">{getPeriodeFromDB(dernierInactif)}</span>
                              ) : isVacant ? '-' : getPeriodeFromDB(membre)
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {afficherInactif ? (
                              <span className="line-through">{getDateDebut(dernierInactif.reunionElection, dernierInactif.dateCooptation)}</span>
                            ) : isVacant ? '-' : getDateDebut(membre.reunionElection, membre.dateCooptation)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {afficherInactif ? (
                              dernierInactif.dateRetrait ? new Date(dernierInactif.dateRetrait).toLocaleDateString('fr-FR') : '-'
                            ) : isVacant ? '-' : (membre.dateRetrait ? new Date(membre.dateRetrait).toLocaleDateString('fr-FR') : '-')}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {afficherInactif ? (
                              <span className="text-red-600 font-medium">
                                {dernierInactif.raisonRetrait === 'demission' ? 'Démission' :
                                 dernierInactif.raisonRetrait === 'non_adhesion' ? 'Non-adhésion' :
                                 dernierInactif.raisonRetrait === 'fin_mandat' ? 'Fin de mandat' :
                                 dernierInactif.raisonRetrait === 'autre' ? dernierInactif.raisonRetraitAutre :
                                 'Poste vacant'}
                              </span>
                            ) : isVacant ? '-' : position.estCoopte || position.estMembreDeDroit ? '-' : getObservationFromDB(membre)}
                          </td>
                        </tr>
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Second Collège (ADAR uniquement) */}
            {organismeActif === 'ADAR' && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Membres du Conseil d'administration issus du Second Collège ({membresSecondCollege.length}/5)
                  </h2>
                  {canModify && organismeActif !== 'ADAR' && (
                    <Link
                      to="/admin/composition-second-college/modifier"
                      className="flex items-center gap-2 px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                      Modifier le Second Collège
                    </Link>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Représentant</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Début</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fin</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Observation</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {/* Membres de droit */}
                      {[
                        { type: 'representant_cooperatives', label: 'Représentant des coopératives' },
                        { type: 'representant_syndicat', label: 'Représentant du syndicat' }
                      ].map((poste) => {
                        const membre = membresSecondCollege.find(m => m.estMembreDeDroitSecondCollege && m.typeMembreDeDroit === poste.type);
                        
                        // Trouver le dernier membre inactif pour ce poste
                        let dernierInactif = null;
                        if (!membre && composition.inactifs) {
                          const inactifsAvecPoste = composition.inactifs.filter(m => 
                            m.type === 'second_college' && 
                            m.estMembreDeDroitSecondCollege && 
                            m.typeMembreDeDroit === poste.type
                          );
                          if (inactifsAvecPoste.length > 0) {
                            dernierInactif = inactifsAvecPoste.sort((a, b) => 
                              new Date(b.dateRetrait) - new Date(a.dateRetrait)
                            )[0];
                          }
                        }
                        
                        const isVacant = !membre;
                        const afficherInactif = isVacant && dernierInactif;
                        
                        return (
                          <tr key={poste.type} className={isVacant ? 'bg-red-50' : 'hover:bg-gray-50'}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-700 font-medium">
                              {poste.label}
                            </td>
                            <td className="px-6 py-4 text-sm">
                              {afficherInactif ? (
                                <span className="text-gray-500 line-through block">
                                  {renderSecondCollegePersonne(dernierInactif)}
                                </span>
                              ) : isVacant ? (
                                <span className="text-gray-400 italic">Vacant</span>
                              ) : (
                                renderSecondCollegePersonne(membre)
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {afficherInactif ? (
                                dernierInactif.reunionElection?.date ? (
                                  <span className="line-through">
                                    {formatReunionElectionLabel(dernierInactif.reunionElection, organismeActif)}{' '}
                                    {new Date(dernierInactif.reunionElection.date).toLocaleDateString('fr-FR')}
                                  </span>
                                ) : '-'
                              ) : membre?.reunionElection?.date ? (
                                <span>
                                  {formatReunionElectionLabel(membre.reunionElection, organismeActif)}{' '}
                                  {new Date(membre.reunionElection.date).toLocaleDateString('fr-FR')}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {afficherInactif ? (
                                dernierInactif.dateRetrait ? new Date(dernierInactif.dateRetrait).toLocaleDateString('fr-FR') : '-'
                              ) : membre?.dateRetrait ? new Date(membre.dateRetrait).toLocaleDateString('fr-FR') : '-'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {afficherInactif ? (
                                <span className="text-red-600 font-medium">
                                  {dernierInactif.raisonRetrait === 'demission' ? 'Démission' :
                                   dernierInactif.raisonRetrait === 'non_adhesion' ? 'Non-adhésion' :
                                   dernierInactif.raisonRetrait === 'autre' ? dernierInactif.raisonRetraitAutre :
                                   'Poste vacant'}
                                </span>
                              ) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                      
                      {/* Séparateur visuel */}
                      <tr className="bg-gray-100">
                        <td colSpan="5" className="px-6 py-2"></td>
                      </tr>
                      
                      {/* Autres membres (3 postes) */}
                      {[1, 2, 3].map((numero) => {
                        const membre = membresSecondCollege.find(m => !m.estMembreDeDroitSecondCollege && m.numeroPosteSecondCollege === numero);
                        
                        // Trouver le dernier membre inactif pour ce poste
                        let dernierInactif = null;
                        if (!membre && composition.inactifs) {
                          const inactifsAvecPoste = composition.inactifs.filter(m => 
                            m.type === 'second_college' && 
                            !m.estMembreDeDroitSecondCollege && 
                            m.numeroPosteSecondCollege === numero
                          );
                          if (inactifsAvecPoste.length > 0) {
                            dernierInactif = inactifsAvecPoste.sort((a, b) => 
                              new Date(b.dateRetrait) - new Date(a.dateRetrait)
                            )[0];
                          }
                        }
                        
                        const isVacant = !membre;
                        const afficherInactif = isVacant && dernierInactif;
                        
                        return (
                          <tr key={`poste-${numero}`} className={isVacant ? 'bg-red-50' : 'hover:bg-gray-50'}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                              {membre?.labelPosteSecondCollege || dernierInactif?.labelPosteSecondCollege || `Poste ${numero}`}
                            </td>
                            <td className="px-6 py-4 text-sm">
                              {afficherInactif ? (
                                <span className="text-gray-500 line-through block">
                                  {renderSecondCollegePersonne(dernierInactif)}
                                </span>
                              ) : isVacant ? (
                                <span className="text-gray-400 italic">Vacant</span>
                              ) : (
                                renderSecondCollegePersonne(membre)
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {afficherInactif ? (
                                dernierInactif.reunionElection?.date ? (
                                  <span className="line-through">
                                    {formatReunionElectionLabel(dernierInactif.reunionElection, organismeActif)}{' '}
                                    {new Date(dernierInactif.reunionElection.date).toLocaleDateString('fr-FR')}
                                  </span>
                                ) : '-'
                              ) : membre?.reunionElection?.date ? (
                                <span>
                                  {formatReunionElectionLabel(membre.reunionElection, organismeActif)}{' '}
                                  {new Date(membre.reunionElection.date).toLocaleDateString('fr-FR')}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {afficherInactif ? (
                                dernierInactif.dateRetrait ? new Date(dernierInactif.dateRetrait).toLocaleDateString('fr-FR') : '-'
                              ) : membre?.dateRetrait ? new Date(membre.dateRetrait).toLocaleDateString('fr-FR') : '-'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {afficherInactif ? (
                                <span className="text-red-600 font-medium">
                                  {dernierInactif.raisonRetrait === 'demission' ? 'Démission' :
                                   dernierInactif.raisonRetrait === 'non_adhesion' ? 'Non-adhésion' :
                                   dernierInactif.raisonRetrait === 'autre' ? dernierInactif.raisonRetraitAutre :
                                   'Poste vacant'}
                                </span>
                              ) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Bureau */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Membres du Bureau ({membresBureau.length})
                </h2>
                {canModify && organismeActif !== 'ADAR' && organismeActif !== 'AMAIR' && organismeActif !== 'SAR' && (
                  <Link
                    to="/admin/composition-bureau/modifier"
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                    Modifier le Bureau
                  </Link>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fonction</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adhérent</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Début</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fin</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Observation</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {positionsBureau.map((position) => {
                      const membre = position.membre;
                      const dernierInactif = position.dernierInactif;
                      const isVacant = !membre;
                      const afficherInactif = isVacant && dernierInactif;
                      
                      return (
                        <tr key={position.fonction} className={isVacant ? 'bg-red-50' : 'hover:bg-gray-50'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {getFonctionLabel(position.fonction)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {afficherInactif ? (
                              <span className="text-gray-500 line-through">
                                {dernierInactif.adherent?.prenom} {dernierInactif.adherent?.nom?.toUpperCase()}
                              </span>
                            ) : isVacant ? (
                              <span className="text-gray-400 italic">Vacant</span>
                            ) : (
                              <span className="text-gray-900">
                                {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {afficherInactif ? (
                              dernierInactif.reunionElection?.date ? (
                                <span className="line-through">
                                  {getReunionTypeShort(dernierInactif.reunionElection.type, organismeActif)}{' '}
                                  {new Date(dernierInactif.reunionElection.date).toLocaleDateString('fr-FR')}
                                </span>
                              ) : '-'
                            ) : isVacant ? '-' : (
                              membre.reunionElection?.date ? (
                                <span>
                                  {formatReunionElectionLabel(membre.reunionElection, organismeActif)}{' '}
                                  {new Date(membre.reunionElection.date).toLocaleDateString('fr-FR')}
                                </span>
                              ) : '-'
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {afficherInactif ? (
                              dernierInactif.dateRetrait ? new Date(dernierInactif.dateRetrait).toLocaleDateString('fr-FR') : '-'
                            ) : isVacant ? '-' : (
                              membre.dateRetrait ? new Date(membre.dateRetrait).toLocaleDateString('fr-FR') : '-'
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {afficherInactif ? (
                              <span className="text-red-600 font-medium">
                                {dernierInactif.raisonRetrait === 'demission' ? 'Démission' :
                                 dernierInactif.raisonRetrait === 'non_adhesion' ? 'Non-adhésion' :
                                 dernierInactif.raisonRetrait === 'fin_mandat' ? 'Fin de mandat' :
                                 dernierInactif.raisonRetrait === 'autre' ? dernierInactif.raisonRetraitAutre :
                                 'Poste vacant'}
                              </span>
                            ) : isVacant ? '-' : (
                              membre.raisonRetrait ? (
                                <span className="text-red-600 font-medium">
                                  {membre.raisonRetrait === 'demission' ? 'Démission' :
                                   membre.raisonRetrait === 'non_adhesion' ? 'Non-adhésion' :
                                   membre.raisonRetrait === 'fin_mandat' ? 'Fin de mandat' :
                                   membre.raisonRetrait === 'autre' ? membre.raisonRetraitAutre :
                                   '-'}
                                </span>
                              ) : '-'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Administrateurs Stagiaires (ADAR uniquement) */}
            {organismeActif === 'ADAR' && (
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Administrateurs Stagiaires ({membresStagiaires.length})
                  </h2>
                  {canModify && organismeActif !== 'ADAR' && (
                    <Link
                      to="/admin/composition-stagiaires/modifier"
                      className="flex items-center gap-2 px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                      Gérer les Stagiaires
                    </Link>
                  )}
                </div>
                {membresStagiaires.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adhérent</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date de nomination</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Réunion de nomination</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {membresStagiaires.map((membre) => (
                          <tr key={membre._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {membre.reunionElection?.date ? new Date(membre.reunionElection.date).toLocaleDateString('fr-FR') : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {membre.reunionElection?.date ? (
                                <span>
                                  {formatReunionElectionLabel(membre.reunionElection, organismeActif)}{' '}
                                  {new Date(membre.reunionElection.date).toLocaleDateString('fr-FR')}
                                </span>
                              ) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-6 text-center text-gray-500">
                    Aucun administrateur stagiaire
                  </div>
                )}
              </div>
            )}

            {/* Anciens membres */}
            {composition.inactifs.length > 0 && (
              <div className="bg-white rounded-lg shadow">
                <button
                  onClick={() => setShowInactifs(!showInactifs)}
                  className="w-full px-6 py-4 border-b border-gray-200 flex items-center justify-between hover:bg-gray-50"
                >
                  <h2 className="text-lg font-semibold text-gray-900">
                    Anciens membres ({composition.inactifs.length})
                  </h2>
                  {showInactifs ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                
                {showInactifs && (
                  <div className="p-6">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Période</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date retrait</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Raison</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {composition.inactifs.map((membre) => {
                            // Déterminer le type
                            let typeLabel = 'Conseil';
                            if (membre.type === 'bureau') typeLabel = 'Bureau';
                            else if (membre.type === 'stagiaire') typeLabel = 'Administrateur stagiaire';
                            else if (membre.type === 'second_college') typeLabel = 'Second collège';
                            else if (membre.estCoopte) typeLabel = 'Coopté';
                            else if (membre.estMembreDeDroit) typeLabel = 'Membre de droit';

                            // Déterminer la position
                            let positionLabel = '-';
                            if (membre.type === 'bureau' && membre.fonction) {
                              positionLabel = getFonctionLabel(membre.fonction);
                            } else if (membre.type === 'conseil' && membre.fraction) {
                              positionLabel = `${getFractionLabel(membre.fraction)} / ${membre.ordre}`;
                            } else if (membre.type === 'second_college') {
                              if (membre.estMembreDeDroitSecondCollege && membre.typeMembreDeDroit) {
                                positionLabel = membre.typeMembreDeDroit === 'representant_cooperatives' 
                                  ? 'Représentant des coopératives' 
                                  : 'Représentant du syndicat';
                              } else if (membre.numeroPosteSecondCollege) {
                                positionLabel = `Poste ${membre.numeroPosteSecondCollege}`;
                              }
                            }

                            return (
                              <tr key={membre._id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {membre.adherent?.prenom} {membre.adherent?.nom}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {typeLabel}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {positionLabel}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {membre.periodeSortie ? 
                                    `${membre.periodeSortie.debut}-${membre.periodeSortie.fin}` : 
                                    '-'
                                  }
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {membre.dateRetrait ? new Date(membre.dateRetrait).toLocaleDateString('fr-FR') : '-'}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {getRaisonRetraitLabel(membre.raisonRetrait, membre.raisonRetraitAutre)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'historique' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Historique des modifications</h2>
            </div>
            <div className="p-6">
              {loadingHistorique ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : historique.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Aucun historique disponible</p>
              ) : (
                <div className="space-y-4">
                  {historique.map((entry) => (
                    <div key={entry._id} className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getActionColor(entry.action)}`}>
                        {getActionLabel(entry.action)}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {entry.adherent?.prenom} {entry.adherent?.nom}
                        </p>
                        <div className="text-sm text-gray-500 mt-1">
                          {entry.typePoste && (
                            <span>
                              {entry.typePoste === 'bureau' ? 'Bureau' : 
                               entry.typePoste === 'coopte' ? 'Coopté' : 
                               entry.typePoste === 'membre_de_droit' ? 'Membre de droit' : 'Conseil'}
                            </span>
                          )}
                          {entry.fonction && (
                            <span> - {getFonctionLabel(entry.fonction)}</span>
                          )}
                          {entry.fraction && (
                            <span> ({getFractionLabel(entry.fraction)})</span>
                          )}
                        </div>
                        {entry.raisonRetrait && (
                          <p className="text-sm text-red-600 mt-1">
                            Raison : {getRaisonRetraitLabel(entry.raisonRetrait, entry.raisonRetraitAutre)}
                          </p>
                        )}
                        {entry.reunion && (
                          <p className="text-xs text-gray-400 mt-1">
                            Réunion du {new Date(entry.reunion.date).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                        {entry.commentaire && (
                          <p className="text-xs text-gray-400 mt-1 italic">{entry.commentaire}</p>
                        )}
                      </div>
                      <div className="text-right text-xs text-gray-400">
                        <p>{new Date(entry.createdAt).toLocaleDateString('fr-FR')}</p>
                        <p>{entry.modifiePar?.prenom} {entry.modifiePar?.nom}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Documents de composition</h2>
              <p className="text-sm text-gray-500 mt-1">
                Un PDF est généré automatiquement après chaque modification du conseil ou du bureau
              </p>
            </div>
            <div className="p-6">
              {loadingDocuments ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : documents.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Aucun document disponible</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fichier</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Taille</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {documents.map((doc) => (
                        <tr key={doc.key} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {new Date(doc.lastModified).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center gap-2">
                              <DocumentTextIcon className="h-5 w-5 text-red-500" />
                              {doc.fileName}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {formatFileSize(doc.size)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <button
                              onClick={() => handleDownloadDocument(doc)}
                              className="inline-flex items-center gap-1 px-3 py-1 text-sm text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                              Télécharger
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal périodes expirées */}
      {showModalPeriodes && (
        <ModalPeriodesExpirees
          periodes={periodesExpirees}
          onTraiter={handleTraiterPeriodes}
          onIgnorer={handleIgnorerPeriodes}
          onClose={() => setShowModalPeriodes(false)}
        />
      )}
    </div>
  );
}
